import firebase from '@/firebase.config'
import Vue from 'vue'

const convertFirestoreTimestamps = (obj) => {

  if (obj === null || typeof obj === 'undefined') {
    console.log('Found null or undefined')
    return obj
  }
  // Search through obj properties for firestore timestamps
  for (const [key, value] of Object.entries(obj)) {

    // Check if the value has a nanoseconds property
    if (value && Object.prototype.hasOwnProperty.call(value, 'nanoseconds')) {
      obj[key] = value.toDate()
    }

    // If the value is an array call this function recursively on each
    if (value && Array.isArray(value)) {
      value.forEach(item => convertFirestoreTimestamps(item))
    }
  }
}

const doc2data = (doc) => {
  let obj = {
    data: doc.data()
  }
  Object.defineProperty(obj, 'id', { value: doc.id })
  Object.defineProperty(obj, 'path', { value: doc.ref.path })

  convertFirestoreTimestamps(obj.data)
  return obj
}

const bindFirestoreRef = async (commit, firestoreRef) => {

  const unbindFunction = await firestoreRef.onSnapshot(res => {
    // May be bound to a collection or an individual document
    if (res.docChanges) {
      // Collection of docs
      const changes = res.docChanges()
      changes.forEach(change => {
        commit('ADD_DOC', change.doc)
      })
    }
    else {
      // Individual doc - res *is* the doc
      commit('ADD_DOC', res)
    }
  })

  commit('ADD_UNBIND_FUNCTION', unbindFunction)
}

export default {
  bindFirestoreRef: bindFirestoreRef,
  doc2data: doc2data,
  state: () => ({
      // path:object map of all objects fetched from firestore
      directory: {},
      // store functions for unbinding
      unbindFunctions: [],
      // db object for convenience
      db: firebase.firestore(),
      // dbRoot object represents the root of the Firestore database
      dbRoot: {}
  }),
  getters: {
    directory(state) {
      return state.directory
    },
    dbRoot(state) {
      return state.dbRoot
    },
    unbindFunctions(state) {
      return state.unbindFunctions
    },
    db(state) {
      return state.db
    }
  },
  mutations: {
    ADD_ROOT_TO_DIRECTORY(state) {
      state.directory['root'] = state.dbRoot
    },
    ADD_DOC(state, firestoreDoc) {
      let doc = doc2data(firestoreDoc)
      // doc is part of a collection within its parent
      let collectionName = firestoreDoc.ref.parent.id
      // Get the parentName, but guard against being at the root of the tree
      let parentName
      if (doc.data.parentPath == 'root') {
        parentName = 'root'
      }
      else {
        // Split the parentPath to find the parent's collection name
        const parentParts = doc.data.parentPath.split('/')
        // Second last element is the collection name
        // Remove the s from the end to unpluralize 
        parentName = (parentParts[parentParts.length - 2]).slice(0, -1)
      }

      // if the doc is already in the directory then this is an update
      if (Object.prototype.hasOwnProperty.call(state.directory, doc.path)) {
        // Replace the data for this doc in the directory
        state.directory[doc.path].data = doc.data
      }

      // Doc is being added for the first time, but can only proceed if its parent has been added
      else if (Object.prototype.hasOwnProperty.call(state.directory, doc.data.parentPath)) {

        // Put this doc into the directory
        state.directory[doc.path] = doc
        const parent = state.directory[doc.data.parentPath] 

        // Create parent collection if it does not exist
        if (!Object.prototype.hasOwnProperty.call(parent, collectionName)) {
          // use Vue.set to initialise array so it is a reactive property
          // https://vuejs.org/v2/api/#Vue-set
          Vue.set(parent, collectionName, [])
        }
        // Add doc to the collection
        parent[collectionName].push(doc)

        // Add a reference back to the parent
        // make them non-enumerable to avoid cirecular references when rendering as JSON
        Object.defineProperty(doc, parentName, { value: parent })
      }

      else {
        // Doc is not added right now, but should be added later by listeners after
        // the hierarchy below it has loaded 
        console.debug('Skipped adding doc without parent', doc)
      }
    },
    ADD_UNBIND_FUNCTION(state, unbindFunction) {
      state.unbindFunctions.push(unbindFunction)
    },
    UNBIND_FIRESTORE_FUNCTIONS(state) {
      while (state.unbindFunctions.length > 0){
        let fn = state.unbindFunctions.pop()
        fn()
      }
    },
    RESET_FIRESTORE_STATE(state) {
      // Get rid of all properties in directory and dbRoot
      Object.keys(state.directory).forEach(key => delete state.directory[key])
      Object.keys(state.dbRoot).forEach(key => delete state.dbRoot[key])
    }
  },
  actions: {
    async addContactMessage({ getters }, contactMessageData) {
      await getters.db.collection('contactMessages').add(contactMessageData)
    },

    // LECTURER AND STUDENT BOTH USE THIS ACTION
    async updateAttempt({ getters }, attempt) {
      await getters.db.doc(attempt.path).set(attempt.data)
    }
  }
}
